<script module>
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   */
</script>

<script>
  import MapComponent from "$lib/components/Map/Map.svelte";
  import DetailHeader from "$lib/components/DetailHeader.svelte";
  import Table from "$lib/components/Table/Table.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import { formatTextTemplate } from "$utils/text.js";
  import { ToolData } from "$utils/data.js";

  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   * @property { boolean } [hideDropdown = false]
   */

  /** @type {MapToolProps} */
  const {
    data,
    tableData,
    configData,
    mapType = "geo",
    showStateSummary,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "Select a metric to view",
    tablePlaceholderLabel = "Select a state or district to see data by county",
    hideDropdown = false
  } = $props();

  // use this effect to load data from the URLs passed as properties
  $effect(() => {
    // fetch primary data and store in $state
    if (!parsedData) {
      parsedData = new ToolData(data, tableData, configData);
    }
    // fetch config data and store in $state
    if (!parsedConfigData) {
      /** @satisfies { import("$utils/config").ColumnConfig[]} */
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  // hard coded values for this instance
  const MAP_COLUMNS = ["meal_perchange_2223"];
  const TOOLTIP_COLUMNS = ["carddelrate_all_24", "del_per_2224", "number_del"];

  /**
   * @param {Record<string, any>[]} allData
   * @param {string} map_id
   * @returns {Record<string, any>[]}
   */
  function getTableData(allData, map_id) {
    return allData.filter((d) => d.map_id === map_id);
  }

  let currentState = $state();
  let currentMetric = $state();
  // use effect to update current Metric to first data column if it isn't defined
  $effect(() => {
    if (parsedData && !currentMetric) {
      currentMetric = parsedData.getDataColumns()[0];
    }
  });

  /**
   * State to hold primary data
   * @type {ToolData | undefined}
   */
  let parsedData = $state();
  // state to hold configuration data
  let parsedConfigData = $state();
  // is the tool using the primary data for the table?
  let tableUsesMainData = $state(false);

  // set the config data into the context so child components can access it without prop drilling
  setConfig(() => parsedConfigData);

  // parse state summary into boolean
  const shouldShowStateSummary = $derived(showStateSummary === "true" && !tableUsesMainData);

  // derived state to hold the current table data, which is filtered based on the state selection
  let currentTableData = $derived(
    parsedData && currentState
      ? parsedData.getStateTableData(currentState, shouldShowStateSummary)
      : []
  );

  // derived state to hold the current map configuration, which is set based on the metric selection
  let currentMapConfig = $derived(
    currentMetric && parsedConfigData ? parsedConfigData.columns.get(currentMetric) : undefined
  );

  const stateDropdownData = getStatesInfo(featureOption).map(({ fips, state }) => ({
    value: fips,
    label: state
  }));

  const currentStateData = $derived(parsedData ? parsedData.getRow(currentState) : undefined);
  const currentStateVariables = $derived(getStateVariables(currentStateData));

  // Extract colNames from parsedData
  let tableColNames = $derived(parsedData ? parsedData.getTableColumns() : []);

  // build dropdown options from colNames
  const dataMetrics = $derived(
    parsedData && parsedConfigData
      ? parsedData
          .getDataColumns()
          .filter((k) => MAP_COLUMNS.includes(k)) // manual override
          .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))
      : []
  );

  /**
   * @param {CustomEvent<{props: {fips: string}}>} e
   */
  function handleStateSelect(e) {
    if (!e) {
      currentState = null;
      return;
    }
    if (e.detail) {
      const { fips, map_id } = e.detail.props;
      currentState = fips || map_id;
    } else {
      currentState = null;
    }
  }

  /**
   * @param {string} metric
   * @param {number} maxWidth
   */
  function getDropdownWidth(metric, maxWidth) {
    return Math.min(220, maxWidth);
  }
  function getStateVariables(stateData) {
    if (!parsedConfigData) {
      return undefined;
    }
    let result = {
      state: getStateName(stateData.map_id)
    };
    for (let key in stateData) {
      let colConfig = parsedConfigData.columns.get(key);
      if (colConfig) {
        result[key] = colConfig.format(stateData[key]);
      } else {
        result[key] = stateData[key];
      }
    }
    return result;
  }
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  <Block width="body"><h4 class="tool-title">Share of consumers with a credit card delinquency</h4></Block>
  <Block width="wide">
    {#if parsedData && parsedConfigData}
      {#if !hideDropdown}
        <div class="tool-header" style={`--accent-color: ${accentColor};`}>
          {dropdownLabel}
          <BasicDropdown
            data={dataMetrics}
            variant="secondary-yellow"
            inlineLabel="Select a metric to view"
            id="metric-select"
            placeholder={null}
            on:change={(e) => {
              // logClickToGA(e.target, "metric-select--" + e.target.value);
            }}
            bind:value={currentMetric}
          />
        </div>
      {/if}
      {#if currentMapConfig}
        <MapComponent
          data={parsedData.data}
          metric={currentMetric}
          onStateSelect={handleStateSelect}
          colorPalette={currentMapConfig.scale_color}
          scaleBreaks={currentMapConfig.scale_breaks}
          scaleType={currentMapConfig.scale_type}
          minMax={currentMapConfig.scale_min_max}
          {featureOption}
          {mapType}
          bind:currentState
          tooltipColumns={TOOLTIP_COLUMNS}
        />
      {/if}
      <div class="state-details">
        <DetailHeader bind:currentState {stateDropdownData} />
        {#if currentState}
          {#if currentStateData && currentStateData.map_description}
            <p>
              {@html formatTextTemplate(currentStateData.map_description, currentStateVariables)}
            </p>
          {/if}
          <Table data={currentTableData} cols={tableColNames} labelColumn={!tableUsesMainData} />
        {:else}
          <p>{tablePlaceholderLabel}</p>
        {/if}
      </div>
    {/if}
  </Block>
</Theme>

<style>
  h4.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--spacing-8);
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .state-details {
    margin: var(--spacing-8) auto;
    background-color: var(--color-gray-shade-lightest);
    padding: var(--spacing-10) var(--spacing-8);
    max-width: var(--size-body-max-width);
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
</style>
